<template>
  <div
    class="w-full lg:grid lg:gap-8 lg:grid-cols-2 grid-rows-1 mx-auto lg:h-full px-5 sm:px-0"
  >
    <div
      class="hidden lg:block w-full lg:h-full"
    >
      <client-only>
        <NuxtImg
          v-show="isMainImageLoaded"
          src="/images/cycle.png"
          class="w-full h-full object-cover"
          width="480"
          height="554"
          format="webp"
          sizes="sm:0px md:0px lg:468px xl:694px 2xl:838px"
          @load="isMainImageLoaded = true"
        />
      </client-only>
      <div
        v-show="!isMainImageLoaded"
        class="h-full w-full bg-gradient-to-r from-07-Grey-300 to-07-Grey-100 animate-pulse"
      />
    </div>
    <div
      class="w-full lg:h-full lg:overflow-y-auto"
    >
      <form
        id="storeSelection"
        v-auto-animate
        class="py-8 w-full max-w-[490px] mx-auto lg:px-1"
      >
        <client-only>
          <CountrySelection
            v-model:selected-country-id="selectedCountryId"
            v-auto-animate
            :countries="countries"
            @update:selected-country-id="resetSelection"
          />
          <PartnerSelection
            v-if="isPartnerSelectionVisible"
            :key="selectedCountryId ?? ''"
            v-model:selected-partner-id="selectedPartnerId"
            v-auto-animate
            :partners="partners"
            @update:selected-partner-id="selectedStore = null"
          />
          <template
            v-if="isStoreSelectionVisible"
          >
            <MainStoreSelection
              v-model:selected-store="selectedStore"
              v-auto-animate
              :stores="sortedStores"
              :loading="isPriceListLoading"
              @continue="onStoreContinue"
            />
          </template>
        </client-only>
        <div
          v-auto-animate
          class="py-8"
        >
          <ErrorSection
            v-if="errors"
            v-auto-animate
            :errors="errors"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { usePriceListStore } from '~/store/price-list-store'
import { useSelectionStore } from '~/store/selection-store'
import type { ErrorData } from '~/types/ErrorData'
import { formatErrors } from '~/utils/erorr-utils'
import type { Partner } from '~/types/Partner'
import type { Country } from '~/types/Country'
import type { Store } from '~/types/Store'
import { countries } from '~/constants'
import CountrySelection from '~/sections/CountrySelection.client.vue'
import PartnerSelection from '~/sections/PartnerSelection.client.vue'
import MainStoreSelection from '~/sections/MainStoreSelection.client.vue'
import ErrorSection from '~/sections/ErrorSection.vue'

definePageMeta({
  middleware: 'index-middleware'
})

const { t } = useI18n()

useHead({ title: computed(() => t('page_title.checkout')) })

const allPartners = computed((): Array<Partner> => countries.find((country: Country) => country.id === selectedCountryId.value)?.partners ?? [])

const partners = computed((): Array<Partner> => allPartners.value.filter((partner: Partner) => partner.visible !== false))

const isPartnerSelectionVisible = computed((): boolean => partners.value.length > 1)

const selectedDomain = computed((): string => {
  if (selectedPartnerId.value) {
    return allPartners.value.find((partner: Partner) => partner.id === selectedPartnerId.value)?.domain ?? 'none'
  }

  if (selectedCountryId.value && !isPartnerSelectionVisible.value) {
    return partners.value[0]?.domain ?? 'none'
  }

  return 'none'
})

const isStoreSelectionVisible = computed((): boolean => selectedDomain.value !== 'none')

const router = useRouter()
const selectionStore = useSelectionStore()
const {
  selectedStore,
  selectedCountryId,
  selectedPartnerId,
  sortedStores
} = storeToRefs(selectionStore)

const priceListStore = usePriceListStore()

const {
  isPriceListLoading
} = storeToRefs(priceListStore)

const errors = ref<ErrorData | undefined>()
const isMainImageLoaded = ref<boolean>(false)

watch(selectedDomain, async (): Promise<void> => {
  if (selectedDomain.value === 'none') {
    return
  }
  const response = await selectionStore.getStores({ subdomain: selectedDomain.value })
  if (response?.error) {
    errors.value = formatErrors(response.error)
  }
},
{ immediate: true })

const resetSelection = (): void => {
  selectedPartnerId.value = null

  selectedStore.value = null
}

const onStoreContinue = (store: Store): void => {
  selectedStore.value = store

  void router.push({ path: '/registration', query: { storeId: store.id } })
}
</script>
